import React, { useEffect, useState } from "react";
import { Check, X, ExternalLink, Inbox, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatDate, formatPHP, formatErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { itemPath, useOpenTarget } from "@/lib/deeplink";
import { PageHeader, Empty } from "@/components/app/Primitives";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

/* One section per kind of waiting item. Every row opens the item itself (in its own company); the
 * header's "Open all" goes to the full page for that kind. The tiles at the top jump to a section. */
const th = (...labels) => (
  <thead className="bg-muted"><tr className="text-left">{labels.map((l) => <th key={l} className="p-2">{l}</th>)}</tr></thead>
);

export default function ApprovalsInbox() {
  const { companies } = useAuth();
  const open = useOpenTarget();
  const several = (companies || []).length > 1;
  // A shared-services team works for every company, so start on all of them.
  const [scope, setScope] = useState(several ? "all" : "company");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  // Says which company an item belongs to (only when looking at all of them).
  const tag = (it) => (scope === "all" && it._company_name
    ? <div className="mt-0.5 inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-700" data-testid="company-tag">{it._company_name}</div>
    : null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/operations/approvals/pending", { params: { scope } });
      setData(data);
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed to load approvals");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [scope]);

  const decide = async (kind, id, decision, companyId) => {
    setBusy(`${kind}:${id}`);
    try {
      let reason = null;
      if (decision === "rejected") {
        reason = window.prompt("Rejection reason?");
        if (!reason) { setBusy(null); return; }
      }
      // Decide in the item's own company, without switching the whole screen to it.
      await api.post(`/operations/approvals/${kind}/${id}/decide`, { decision, reason },
        companyId ? { headers: { "X-Company-Id": companyId } } : undefined);
      toast.success(`Marked ${decision}`);
      await load();
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(null);
    }
  };

  const goItem = (kind, it) => open(itemPath(kind, it), it._company_id);
  const goAll = (path) => open(path, null);

  const deciders = (kind, it) => (
    <div className="flex gap-1">
      <Button size="sm" disabled={busy === `${kind}:${it.id}`}
        onClick={(e) => { e.stopPropagation(); decide(kind, it.id, "approved", it._company_id); }}>
        <Check className="size-3 mr-1" /> Approve
      </Button>
      <Button size="sm" variant="destructive" disabled={busy === `${kind}:${it.id}`}
        onClick={(e) => { e.stopPropagation(); decide(kind, it.id, "rejected", it._company_id); }}>
        <X className="size-3 mr-1" /> Reject
      </Button>
    </div>
  );
  const openBtn = (kind, it, label = "Open") => (
    <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); goItem(kind, it); }}>
      {label} <ExternalLink className="size-3 ml-1" />
    </Button>
  );
  const person = (it) => (
    <td className="p-2">
      <div className="font-medium">{it._employee_name || it.employee_name || "—"}</div>
      <div className="text-xs text-muted-foreground">{it._employee_no || it.employee_no || ""}</div>
      {tag(it)}
    </td>
  );

  // key = field in the response, kind = item kind for links and decisions
  const SECTIONS = [
    { key: "leaves", kind: "leave", title: "Leave Requests", all: "/timekeeping/leaves", tile: "Leaves",
      head: th("Employee", "Type", "Period", "Days", "Reason", "Actions"),
      row: (it) => (<>{person(it)}
        <td className="p-2 capitalize">{it.leave_type}</td>
        <td className="p-2 text-xs">{formatDate(it.start_date)} → {formatDate(it.end_date)}</td>
        <td className="p-2">{it.days}</td>
        <td className="p-2 text-xs text-muted-foreground">{it.reason || "—"}</td>
        <td className="p-2">{deciders("leave", it)}</td></>) },
    { key: "overtime", kind: "overtime", title: "Overtime Requests", all: "/timekeeping/overtime", tile: "Overtime",
      head: th("Employee", "Date", "Hours", "Reason", "Actions"),
      row: (it) => (<>{person(it)}
        <td className="p-2">{formatDate(it.work_date)}</td>
        <td className="p-2">{it.hours}</td>
        <td className="p-2 text-xs text-muted-foreground">{it.reason || "—"}</td>
        <td className="p-2">{deciders("overtime", it)}</td></>) },
    { key: "attendance_corrections", kind: "attendance", title: "Attendance Corrections", all: "/timekeeping/attendance", tile: "Att. Corrections",
      head: th("Employee", "Date", "Time In / Out", "Note", "Actions"),
      row: (it) => (<>{person(it)}
        <td className="p-2">{it.work_date}</td>
        <td className="p-2 text-xs">{it.time_in || "—"} / {it.time_out || "—"}</td>
        <td className="p-2 text-xs text-muted-foreground">{it.note || "manual entry"}</td>
        <td className="p-2">{deciders("attendance", it)}</td></>) },
    { key: "payroll_runs", kind: "payroll", title: "Payroll Runs Awaiting Action", all: "/payroll/control", tile: "Payroll", empty: "No payroll runs in review state.",
      head: th("Period", "Status", "Net", "Employees", ""),
      row: (it) => (<>
        <td className="p-2">{it.period_start} → {it.period_end}{tag(it)}</td>
        <td className="p-2"><Badge variant="outline" className="capitalize">{it.status?.replaceAll("_", " ")}</Badge></td>
        <td className="p-2">{formatPHP(it.total_net)}</td>
        <td className="p-2">{it.employee_count || 0}</td>
        <td className="p-2">{openBtn("payroll", it, "Open run")}</td></>) },
    { key: "helpdesk", kind: "helpdesk", title: "Open Helpdesk Tickets", all: "/helpdesk", tile: "Helpdesk", empty: "No open tickets.",
      head: th("Subject", "Employee", "Category", "Priority", "Status", ""),
      row: (it) => (<>
        <td className="p-2 font-medium">{it.subject}{tag(it)}</td>
        <td className="p-2 text-xs">{it._employee_name || "—"}</td>
        <td className="p-2 capitalize">{it.category}</td>
        <td className="p-2 capitalize">{it.priority}</td>
        <td className="p-2 capitalize">{it.status}</td>
        <td className="p-2">{openBtn("helpdesk", it)}</td></>) },
    { key: "document_requests", kind: "document", title: "Document Requests", all: "/hris/document-requests", tile: "Documents", empty: "No document requests waiting.",
      head: th("Employee", "Document", "Status", ""),
      row: (it) => (<>{person(it)}
        <td className="p-2">{it.doc_label || String(it.doc_type || "").replaceAll("_", " ")}</td>
        <td className="p-2 capitalize">{it.status}</td>
        <td className="p-2">{openBtn("document", it)}</td></>) },
    { key: "time_adjustments", kind: "time_adjustment", title: "Time Corrections", all: "/timekeeping/adjustments", tile: "Time Corrections", empty: "No time corrections waiting.",
      head: th("Employee", "Date", "Asked for", "Reason", ""),
      row: (it) => (<>{person(it)}
        <td className="p-2">{it.work_date}</td>
        <td className="p-2 text-xs">{it.requested_time_in || "—"} / {it.requested_time_out || "—"}</td>
        <td className="p-2 text-xs text-muted-foreground">{it.reason || "—"}</td>
        <td className="p-2">{openBtn("time_adjustment", it)}</td></>) },
    { key: "schedule_adjustments", kind: "schedule_adjustment", title: "Shift Changes", all: "/timekeeping/adjustments", tile: "Shift Changes", empty: "No shift changes waiting.",
      head: th("Employee", "Effective", "Reason", ""),
      row: (it) => (<>{person(it)}
        <td className="p-2">{it.effective_date || "—"}</td>
        <td className="p-2 text-xs text-muted-foreground">{it.reason || "—"}</td>
        <td className="p-2">{openBtn("schedule_adjustment", it)}</td></>) },
    { key: "candidates_offer", kind: "candidate", title: "Candidates in Offer Stage", all: "/recruitment", tile: "Offers", empty: "No candidates pending offer.",
      head: th("Name", "Email", "Stage", ""),
      row: (it) => (<>
        <td className="p-2 font-medium">{it.name || [it.first_name, it.last_name].filter(Boolean).join(" ")}{tag(it)}</td>
        <td className="p-2 text-xs">{it.email}</td>
        <td className="p-2 capitalize">{it.stage}</td>
        <td className="p-2">{openBtn("candidate", it)}</td></>) },
    { key: "onboarding", kind: "onboarding", title: "Onboarding in Progress", all: "/onboarding", tile: "Onboarding", tileOnly: true },
    { key: "final_pay", kind: "final_pay", title: "Final Pay", all: "/payroll/final-pay", tile: "Final Pay", tileOnly: true },
  ];

  const jump = (key, s) => {
    const el = document.getElementById(`inbox-${key}`);
    if (el && !s.tileOnly) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.classList.add("focus-flash");
      setTimeout(() => el.classList.remove("focus-flash"), 2500);
    } else {
      goAll(s.all);   // these two have no section here: the tile opens their own page
    }
  };

  if (loading && !data) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  if (!data) return <Empty title="No data" />;
  const t = data.totals || {};

  return (
    <div className="space-y-6">
      <PageHeader
        title="Approvals Inbox"
        subtitle="Everything awaiting HR or management decision. Click a tile to jump to it, or any row to open the item."
        actions={
          <div className="flex items-center gap-2">
            {several && (
              <select className="h-9 rounded-md border border-border bg-background px-2 text-sm" value={scope} onChange={(e) => setScope(e.target.value)} data-testid="inbox-scope" aria-label="Which companies to show">
                <option value="all">All my companies</option>
                <option value="company">This company only</option>
              </select>
            )}
            <Button variant="outline" onClick={load}>
              <RefreshCw className="size-4 mr-1" /> Refresh
            </Button>
          </div>
        }
      />

      <div className="grid gap-3 grid-cols-2 md:grid-cols-4 xl:grid-cols-6" data-testid="inbox-tiles">
        {SECTIONS.map((s) => (
          <button key={s.key} type="button" onClick={() => jump(s.key, s)} data-testid={`tile-${s.key}`}
            className="text-left rounded-xl border border-border bg-card p-4 hover:border-primary hover:shadow-sm transition focus:outline-none focus:ring-2 focus:ring-primary/40">
            <div className="flex items-start justify-between">
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{s.tile}</div>
              <Inbox className="size-4 text-muted-foreground" />
            </div>
            <div className="mt-2 text-2xl font-bold font-display">{t[s.key] || 0}</div>
          </button>
        ))}
      </div>

      {SECTIONS.filter((s) => !s.tileOnly).map((s) => {
        const items = data[s.key] || [];
        return (
          <Card key={s.key} id={`inbox-${s.key}`} className="scroll-mt-4" data-testid={`section-${s.key}`}>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">
                {s.title} <span className="ml-2 text-xs text-muted-foreground">({items.length})</span>
              </CardTitle>
              <button type="button" className="text-xs text-primary hover:underline inline-flex items-center gap-1" onClick={() => goAll(s.all)} data-testid={`open-all-${s.key}`}>
                Open all <ExternalLink className="size-3" />
              </button>
            </CardHeader>
            <CardContent>
              {!items.length && <Empty title={s.empty || "No pending items"} />}
              {!!items.length && (
                <div className="overflow-auto rounded-md border">
                  <table className="w-full text-sm">
                    {s.head}
                    <tbody>
                      {items.map((it) => (
                        <tr key={it.id} className="border-t cursor-pointer hover:bg-secondary/50" onClick={() => goItem(s.kind, it)} data-testid={`row-${s.kind}-${it.id}`}>
                          {s.row(it)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
